import { ApolloLink, type FetchResult, type NextLink, type Observable, type Operation } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import type { FreshnessCache } from './freshnessCache';

/**
 * Pone edad a lo que llega de la RED (spec 2026-10-01 § 7, Fase 3b).
 *
 * Va en la cadena JUSTO DEBAJO de `OfflineCacheFallbackLink`: todo lo que sube
 * por aquí viene del servidor. Lo que sirve el respaldo desde la caché no pasa
 * por este enlace, y por eso nunca rejuvenece un dato caducado.
 *
 * Solo MARCA el objeto `result.data` (`markFromNetwork`): la edad se pone cuando
 * Apollo lo escribe en la caché, con el documento original (que conserva
 * `@connection`). Una consulta `no-cache` nunca se escribe y no deja edad.
 *
 * Solo consultas y solo respuestas sin errores: una respuesta parcial con
 * `errors` no prueba que el dato esté al día.
 */
export class FreshnessLink extends ApolloLink {
  private readonly freshnessCache: FreshnessCache;

  constructor(cache: FreshnessCache) {
    super();
    this.freshnessCache = cache;
  }

  request(operation: Operation, forward: NextLink): Observable<FetchResult> {
    const definition = getMainDefinition(operation.query);
    const isQuery = definition.kind === 'OperationDefinition' && definition.operation === 'query';
    if (!isQuery) return forward(operation);
    return forward(operation).map((result) => {
      if (result.data && typeof result.data === 'object' && !result.errors?.length) {
        this.freshnessCache.markFromNetwork(result.data as object);
      }
      return result;
    });
  }
}
